import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import useHandleErrors from '../../utils/user.utils';
import { useUser } from '../../utils/userContext';
import { Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/travelrequest.css';


const travelTypes = [
  { type: 'Bus/Taxi', emoji: '🚌' },
  { type: 'Train', emoji: '🚆' },
  { type: 'Flights', emoji: '✈️' },
  { type: 'Hotels', emoji: '🏨' }
];


const cabTypes = [
  { value: 'One Way', label: 'One Way' },
  { value: 'Round Trip', label: 'Round Trip' },
  { value: 'City', label: 'City' }
];


function getDefaultTabForms() {
  return {
    'Bus/Taxi': {
      travelfrom: '', travelto: '',
      travelstartdate: '', travelenddate: '',
      numberofdays: '',
      busCabType: 'One Way',
      description: '', approvedby: ''
    },
    'Train': {
      travelfrom: '', travelto: '',
      travelstartdate: '', travelenddate: '',
      trainTripType: 'One Way',
      description: '', approvedby: ''
    },
    'Flights': {
      travelfrom: '', travelto: '',
      travelstartdate: '', travelenddate: '',
      numberofdays: '',
      flightsTripType: 'One Way',
      description: '', approvedby: ''
    },
    'Hotels': {
      travelfrom: '', // city
      travelstartdate: '', travelenddate: '',
      numberofdays: '',
      description: '', approvedby: ''
    }
  };
}


const tripTypes = [
  { value: 'One Way', label: 'One Way' },
  { value: 'Round Trip', label: 'Round Trip' }
];


const TravelRequestForm = () => {
  const { user } = useUser();
  const token = localStorage.getItem('token');
  const handleErrors = useHandleErrors();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const hasFetchedRef = useRef(false);


  const [tabForms, setTabForms] = useState(getDefaultTabForms());
  const [activeType, setActiveType] = useState('Bus/Taxi');
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);


  const assignToOptions = employees
    .filter(e => e.UserAccessLevel === 'Admin')
    .map(e => ({ value: e.OfficialEmail, label: e.OfficialEmail }));


  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchEmployees();
      hasFetchedRef.current = true;
    }
    // eslint-disable-next-line
  }, []);


  async function fetchEmployees() {
    setLoading(true);
    try {
      const URL = process.env.REACT_APP_BE_URL + "/getEmployeeMasterReport";
      const response = await axios.get(URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200 && response.data.onboardEmployeeDetails) {
        setEmployees(response.data.onboardEmployeeDetails.filter(emp => emp.Status === "Active"));
      } else {
        handleErrors(response);
      }
    } catch (err) {
      if (err.response) handleErrors(err.response);
      else toast.error("Unable to fetch approvers.", { position: toast.POSITION.TOP_CENTER });
    } finally { setLoading(false); }
  }


  function fetchSuggestions(query, field) {
    if (!query || query.length < 1) {
      if (field === 'from') setFromSuggestions([]); else setToSuggestions([]);
      return;
    }
    clearTimeout(window[`_${field}Timeout`]);
    window[`_${field}Timeout`] = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`
        );
        const data = await response.json();
        const suggestions = data.map(item => {
          const { city, town, village, state } = item.address;
          const location = [city || town || village, state].filter(Boolean).join(', ');
          return location || item.display_name;
        });
        if (field === 'from') setFromSuggestions(suggestions); else setToSuggestions(suggestions);
      } catch {
        toast.error('Failed to fetch location suggestions.', { position: toast.POSITION.TOP_CENTER });
        if (field === 'from') setFromSuggestions([]); else setToSuggestions([]);
      }
    }, 250);
  }


  function handleChangeWithSuggestions(e, field) {
    const { name, value } = e.target;
    setTabForms(prev => {
      const tabForm = { ...prev[activeType], [name]: value };
      // For date fields, update days for relevant
      if ((name === 'travelstartdate' || name === 'travelenddate')
        && ['Bus/Taxi', 'Train', 'Flights', 'Hotels'].includes(activeType)
      ) {
        tabForm.numberofdays = calculateDays(
          name === 'travelstartdate' ? value : tabForm.travelstartdate,
          name === 'travelenddate' ? value : tabForm.travelenddate
        );
      }
      return { ...prev, [activeType]: tabForm };
    });
    fetchSuggestions(value, field);
  }


  function handleSelectSuggestion(value, field) {
    setTabForms(prev => {
      const tabForm = { ...prev[activeType] };
      if (field === 'from') tabForm.travelfrom = value; else tabForm.travelto = value;
      return { ...prev, [activeType]: tabForm };
    });
    if (field === 'from') setFromSuggestions([]); else setToSuggestions([]);
  }


  function handleInput(e) {
    const { name, value, type } = e.target;
    setTabForms(prev => {
      let tabForm = { ...prev[activeType], [name]: type === 'number' ? Number(value) : value };
      // If editing date, recalculate numberofdays
      if ((name === 'travelstartdate' || name === 'travelenddate')
        && ['Bus/Taxi', 'Train', 'Flights', 'Hotels'].includes(activeType)
      ) {
        tabForm.numberofdays = calculateDays(
          name === 'travelstartdate' ? value : tabForm.travelstartdate,
          name === 'travelenddate' ? value : tabForm.travelenddate
        );
      }
      return { ...prev, [activeType]: tabForm };
    });
  }


  function handleBusCabTypeChange(value) {
    setTabForms(prev => {
      const tabForm = { ...prev[activeType], busCabType: value };
      if (value !== 'Round Trip') tabForm.travelenddate = '';
      return { ...prev, [activeType]: tabForm };
    });
  }


  function handleTripTypeChange(typeField, value) {
    setTabForms(prev => {
      const tabForm = { ...prev[activeType], [typeField]: value };
      if (value !== 'Round Trip' && value !== 'One Way') tabForm.travelenddate = '';
      return { ...prev, [activeType]: tabForm };
    });
  }


  function handleTabSwitch(type) {
    setActiveType(type);
    setFromSuggestions([]);
    setToSuggestions([]);
  }


  function calculateDays(start, end) {
    if (!start || !end) return '';
    const d1 = new Date(start), d2 = new Date(end);
    if (activeType === 'Hotels' && d1.toDateString() === d2.toDateString()) {
      return '1';
    }
    if (d2 < d1) return '';
    return (Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)) + 1).toString();
  }


  function validateForm(tab, tabForm) {
    // Bus/Taxi
    if (tab === 'Bus/Taxi') {
      if (!tabForm.travelfrom) { toast.error('From is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (tabForm.busCabType !== 'City' && !tabForm.travelto) { toast.error('To is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.travelstartdate) { toast.error('Travel Date required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (tabForm.busCabType === 'Round Trip' && !tabForm.travelenddate) {
        toast.error('Return Date required for Round Trip.', { position: toast.POSITION.TOP_CENTER }); return false;
      }
      if (tabForm.travelfrom === tabForm.travelto && tabForm.busCabType !== 'City') {
        toast.error('Pick up and drop locations must be different.', { position: toast.POSITION.TOP_CENTER }); return false;
      }
      if (!tabForm.approvedby) {
        toast.error('Approver is required.', { position: toast.POSITION.TOP_CENTER }); return false;
      }
    }
    // Train
    if (tab === 'Train') {
      if (!tabForm.travelfrom) { toast.error('From is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.travelto) { toast.error('To is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.travelstartdate) { toast.error('Travel Date required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      // if (tabForm.trainTripType === 'Round Trip' && !tabForm.travelenddate) {
      //   toast.error('Return Date required for Round Trip.', { position: toast.POSITION.TOP_CENTER }); return false;
      // }
      if (!tabForm.approvedby) {
        toast.error('Approver is required.', { position: toast.POSITION.TOP_CENTER }); return false;
      }
    }
    // Flights
    if (tab === 'Flights') {
      if (!tabForm.travelfrom) { toast.error('From is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.travelto) { toast.error('To is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.travelstartdate) { toast.error('Departure Date required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (tabForm.flightsTripType === 'Round Trip' && !tabForm.travelenddate) {
        toast.error('Return Date required for Round Trip.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (tabForm.travelfrom === tabForm.travelto) {
        toast.error('Pick up and drop locations must be different.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.approvedby) {
        toast.error('Approver is required.', { position: toast.POSITION.TOP_CENTER }); return false;
      }
    }
    // Hotels
    if (tab === 'Hotels') {
      if (!tabForm.travelfrom) { toast.error('City is required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.travelstartdate) { toast.error('Check-in Date required.', { position: toast.POSITION.TOP_CENTER }); return false; }
      if (!tabForm.approvedby) {
        toast.error('Approver is required.', { position: toast.POSITION.TOP_CENTER }); return false;
      }
    }
    return true;
  }


  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const tabForm = tabForms[activeType];
    if (!validateForm(activeType, tabForm)) { setLoading(false); return; }
    // Validation: End date must be greater than or equal to start date for hotels, greater for others
    if (tabForm.travelstartdate && tabForm.travelenddate) {
      const start = new Date(tabForm.travelstartdate);
      const end = new Date(tabForm.travelenddate);
      if (activeType === 'Hotels') {
        if (end < start) {
          toast.error("Hotel End Date must be greater than or equal to Hotel Start Date.", {
            position: toast.POSITION.TOP_CENTER,
          });
          setLoading(false);
          return;
        }
      } else {
        if (end <= start) {
          toast.error("Travel End Date must be greater than Travel Start Date.", {
            position: toast.POSITION.TOP_CENTER,
          });
          setLoading(false);
          return;
        }
      }
    }
    // For travel types with tripType (Bus/Taxi, Flights), also check if Round Trip is selected and end date is present
    if ((activeType === 'Bus/Taxi' && tabForm.busCabType === 'Round Trip' && tabForm.travelenddate) ||
      // (activeType === 'Train' && tabForm.trainTripType === 'Round Trip' && tabForm.travelenddate) ||
      (activeType === 'Flights' && tabForm.flightsTripType === 'Round Trip' && tabForm.travelenddate)) {
      const start = new Date(tabForm.travelstartdate);
      const end = new Date(tabForm.travelenddate);
      if (end <= start) {
        toast.error("Travel End Date must be greater than Travel Start Date.", {
          position: toast.POSITION.TOP_CENTER,
        });
        setLoading(false);
        return;
      }
    }


    try {
      const formData = new FormData();
      formData.append('EmployeeId', user?.EmployeeId);
      formData.append('EmployeeName', user?.FirstName + ' ' + user?.LastName);
      formData.append('TypeofTravel', activeType);
      if (activeType === 'Bus/Taxi') {
        formData.append('TravelFrom', tabForm.travelfrom);
        formData.append('TravelTo', tabForm.travelto);
        formData.append('TravelStartDate', tabForm.travelstartdate);

        if (tabForm.busCabType === 'Round Trip' && tabForm.travelenddate) {
          formData.append('TravelEndDate', tabForm.travelenddate);
        }
        else {
          formData.append('TravelEndDate', '');
        }
        formData.append('NumberOfDays', tabForm.numberofdays || '1');
        formData.append('TripType', tabForm.busCabType);
        formData.append('Description', tabForm.description || '');
        formData.append('ApprovedBy', tabForm.approvedby);
        formData.append('Status', 'New');
      }
      if (activeType === 'Train') {
        formData.append('TravelFrom', tabForm.travelfrom);
        formData.append('TravelTo', tabForm.travelto);
        formData.append('TravelStartDate', tabForm.travelstartdate);
        formData.append('TripType', "One Way");
        formData.append('Description', tabForm.description || '');
        formData.append('ApprovedBy', tabForm.approvedby);
        formData.append('Status', 'New');
      }
      if (activeType === 'Flights') {
        formData.append('TravelFrom', tabForm.travelfrom);
        formData.append('TravelTo', tabForm.travelto);
        formData.append('TravelStartDate', tabForm.travelstartdate);
        if (tabForm.flightsTripType === 'Round Trip' && tabForm.travelenddate) {
          formData.append('TravelEndDate', tabForm.travelenddate);
        }
        formData.append('TripType', tabForm.flightsTripType);
        formData.append('NumberOfDays', tabForm.numberofdays || '1');
        formData.append('Description', tabForm.description || '');
        formData.append('ApprovedBy', tabForm.approvedby);
        formData.append('Status', 'New');
      }
      if (activeType === 'Hotels') {
        formData.append('TravelFrom', tabForm.travelfrom);
        formData.append('TravelStartDate', tabForm.travelstartdate);
        if (tabForm.travelenddate) {
          formData.append('TravelEndDate', tabForm.travelenddate);
        }
        formData.append('NumberOfDays', tabForm.numberofdays || '1');
        formData.append('Description', tabForm.description || '');
        formData.append('ApprovedBy', tabForm.approvedby);
        formData.append('Status', 'New');
        formData.append('TripType', 'Stay');
      }
      const URL = process.env.REACT_APP_BE_URL + "/travelDesk";
      await axios.post(URL, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Request submitted successfully!', { position: toast.POSITION.TOP_CENTER });
      clearForm();
    } catch (err) {
      if (err.response) handleErrors(err.response);
      else toast.error("Error submitting request.", { position: toast.POSITION.TOP_CENTER });
    } finally { setLoading(false); }
  }


  function clearForm() {
    setTabForms(getDefaultTabForms());
    setFromSuggestions([]);
    setToSuggestions([]);
  }


  function renderFormFields(tab, tabForm) {
    // === BUS/TAXI ===
    if (tab === 'Bus/Taxi') {
      return (
        <>
          <div className="input-row cabtype-row" style={{ display: 'flex', alignItems: 'center' }}>
            <label className="cabtype-label" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>
              Bus/Taxi Type:
            </label>
            <div className="dot-buttons" style={{ display: 'flex', gap: '15px' }}>
              {cabTypes.map(({ value, label }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="radio"
                    name="busCabType"
                    value={value}
                    checked={tabForm.busCabType === value}
                    onChange={() => handleBusCabTypeChange(value)}
                  />
                  <span className="dot"></span> {label}
                </label>
              ))}
            </div>
          </div>

          {/* Locations */}
          <div className="input-row">
            <div className="input-box autocomplete">
              <input
                type="text"
                name="travelfrom"
                value={tabForm.travelfrom}
                onChange={e => handleChangeWithSuggestions(e, 'from')}
                autoComplete="off"
                placeholder=" "
              />
              <label>From</label>
              {fromSuggestions.length > 0 && (
                <ul className="suggestions">
                  {fromSuggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(s, 'from')}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
            {tabForm.busCabType !== 'City' && (
              <>
                {tabForm.busCabType === 'Round Trip' && (
                  <i className="ri-arrow-left-right-line" style={{ margin: '0 10px', display: 'flex', alignItems: 'center' }}></i>
                )}
                <div className="input-box autocomplete">
                  <input
                    type="text"
                    name="travelto"
                    value={tabForm.travelto}
                    onChange={e => handleChangeWithSuggestions(e, 'to')}
                    autoComplete="off"
                    placeholder=" "
                  />
                  <label>To</label>
                  {toSuggestions.length > 0 && (
                    <ul className="suggestions">
                      {toSuggestions.map((s, i) => (
                        <li key={i} onClick={() => handleSelectSuggestion(s, 'to')}>{s}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="input-row">
            <div className="input-box">
              <input
                type="date"
                name="travelstartdate"
                value={tabForm.travelstartdate}
                onChange={handleInput}
                placeholder=" "
              />
              <label>Travel Date</label>
            </div>
            {tabForm.busCabType === 'Round Trip' &&
              <div className="input-box">
                <input
                  type="date"
                  name="travelenddate"
                  value={tabForm.travelenddate}
                  onChange={handleInput}
                  placeholder=" "
                />
                <label>Return Date</label>
              </div>
            }
          </div>
          {/* Only show Number of Days if Round Trip */}
          {tabForm.busCabType === 'Round Trip' && (
            <div className="input-row">
              <div className="input-box">
                <input type="number" name="numberofdays" value={tabForm.numberofdays} readOnly />
                <label>Number of Days</label>
              </div>
            </div>
          )}
        </>
      );
    }
    // === TRAIN ===
    if (tab === 'Train') {
      return (
        <>
          <div className="input-row triptype-row">

          </div>
          <div className="input-row">
            <div className="input-box autocomplete">
              <input
                type="text"
                name="travelfrom"
                value={tabForm.travelfrom}
                onChange={e => handleChangeWithSuggestions(e, 'from')}
                autoComplete="off"
                placeholder=" "
              />
              <label>From</label>
              {fromSuggestions.length > 0 && (
                <ul className="suggestions">
                  {fromSuggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(s, 'from')}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="input-box autocomplete">
              <input
                type="text"
                name="travelto"
                value={tabForm.travelto} // Changed from travelfrom to travelto
                onChange={e => handleChangeWithSuggestions(e, 'to')}
                autoComplete="off"
                placeholder=" "
              />
              <label>To</label>
              {toSuggestions.length > 0 && (
                <ul className="suggestions">
                  {toSuggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(s, 'to')}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="input-row">
            <div className="input-box">
              <input
                type="date"
                name="travelstartdate"
                value={tabForm.travelstartdate}
                onChange={handleInput}
                placeholder=" "
              />
              <label>Travel Date</label>
            </div>

          </div>
        </>
      );
    }
    // === FLIGHTS ===
    if (tab === 'Flights') {
      return (
        <>
          <div className="input-row triptype-row" style={{ display: 'flex', alignItems: 'center' }}>
            <label className="cabtype-label" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>
              Trip Type:
            </label>
            <div className="dot-buttons" style={{ display: 'flex', gap: '15px' }}>
              {tripTypes.map(({ value, label }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="radio"
                    name="flightsTripType"
                    value={value}
                    checked={tabForm.flightsTripType === value}
                    onChange={() => handleTripTypeChange('flightsTripType', value)}
                  />
                  <span className="dot"></span> {label}
                </label>
              ))}
            </div>
          </div>

          <div className="input-row">
            <div className="input-box autocomplete">
              <input
                type="text"
                name="travelfrom"
                value={tabForm.travelfrom}
                onChange={e => handleChangeWithSuggestions(e, 'from')}
                autoComplete="off"
                placeholder=" "
              />
              <label>From</label>
              {fromSuggestions.length > 0 && (
                <ul className="suggestions">
                  {fromSuggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(s, 'from')}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
            {tabForm.flightsTripType === 'Round Trip' && (
              <i className="ri-arrow-left-right-line" style={{ margin: '0 10px', display: 'flex', alignItems: 'center' }}></i>
            )}
            <div className="input-box autocomplete">
              <input
                type="text"
                name="travelto"
                value={tabForm.travelto}
                onChange={e => handleChangeWithSuggestions(e, 'to')}
                autoComplete="off"
                placeholder=" "
              />
              <label>To</label>
              {toSuggestions.length > 0 && (
                <ul className="suggestions">
                  {toSuggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(s, 'to')}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="input-row">
            <div className="input-box">
              <input
                type="date"
                name="travelstartdate"
                value={tabForm.travelstartdate}
                onChange={handleInput}
                placeholder=" "
              />
              <label>Departure Date</label>
            </div>
            {tabForm.flightsTripType === 'Round Trip' &&
              <div className="input-box">
                <input
                  type="date"
                  name="travelenddate"
                  value={tabForm.travelenddate}
                  onChange={handleInput}
                  placeholder=" "
                />
                <label>Return Date</label>
              </div>
            }
          </div>
          {/* Only show Number of Days if NOT One Way */}
          {tabForm.flightsTripType !== 'One Way' && (
            <div className="input-row">
              <div className="input-box">
                <input type="number" name="numberofdays" value={tabForm.numberofdays} readOnly />
                <label>Number of Days</label>
              </div>
            </div>
          )}
        </>
      );
    }
    // === HOTELS ===
    if (tab === 'Hotels') {
      return (
        <>
          <div className="input-row">
            <div className="input-box autocomplete">
              <input
                type="text"
                name="travelfrom"
                value={tabForm.travelfrom}
                onChange={e => handleChangeWithSuggestions(e, 'from')}
                autoComplete="off"
                placeholder=" "
              />
              <label>City</label>
              {fromSuggestions.length > 0 && (
                <ul className="suggestions">
                  {fromSuggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(s, 'from')}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="input-row">
            <div className="input-box">
              <input
                type="date"
                name="travelstartdate"
                value={tabForm.travelstartdate}
                onChange={handleInput}
                placeholder=" "
              />
              <label>Check-in Date</label>
            </div>
            <div className="input-box">
              <input
                type="date"
                name="travelenddate"
                value={tabForm.travelenddate}
                onChange={handleInput}
                placeholder=" "
              />
              <label>Check-out Date</label>
            </div>
          </div>
          <div className="input-row">
            <div className="input-box">
              <input type="number" name="numberofdays" value={tabForm.numberofdays} readOnly />
              <label>Number of Days</label>
            </div>
          </div>
        </>
      );
    }
    return null;
  }


  const tabForm = tabForms[activeType];


 return (
  <div>
    {loading && (
      <div id="preloading">
        <span className="loader"></span>
      </div>
    )}
    <ToastContainer />

    {/* Floating My Orders button */}
    <div
      style={{
        position: 'fixed',
        top: '80px',
        right: '15px',
        zIndex: 9999
      }}
    >
      <Link
        to={'/My-Orders'}
        className="btn btn-primary"
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <i className="ri-suitcase-3-fill" style={{ marginRight: '5px' }}></i>
        My Orders
      </Link>
    </div>

    <div className="trip-container">
      <form className="trip-form" onSubmit={handleSubmit} autoComplete="off">
        <h2>Travel Desk</h2>
        <div className="travel-tabs">
          {travelTypes.map(tab => (
            <div
              key={tab.type}
              className={`tab ${activeType === tab.type ? `active animated-${tab.type.toLowerCase().replace('/', '-')}` : ''}`}
              onClick={() => handleTabSwitch(tab.type)}
            >
              <span className="emoji">{tab.emoji}</span>
              <span className="label">{tab.type}</span>
            </div>
          ))}
        </div>
        {renderFormFields(activeType, tabForm)}

        <div className="input-row">
          <div className="input-box full approver-select-wrapper">
            <label className="approver-label">Approver</label>
            <Select
              id="approvedby"
              name="approvedby"
              options={assignToOptions}
              value={assignToOptions.find(opt => opt.value === tabForm.approvedby) || null}
              onChange={(selected) =>
                handleInput({ target: { name: 'approvedby', value: selected?.value || '' } })
              }
              placeholder="Select Approver"
              isSearchable={true}
              isClearable
              classNamePrefix="react-select"
              className="react-select-container"
            />
          </div>
        </div>

        <div className="input-box full">
          <textarea
            name="description"
            rows="3"
            value={tabForm.description}
            onChange={handleInput}
            placeholder=" "
          />
          <label>Description</label>
        </div>

        <div className="form-buttons">
          <button type="submit" className="book-btn">Submit</button>
          <button type="button" className="clear-btn" onClick={clearForm}>Clear</button>
        </div>
      </form>
    </div>
  </div>
);

};



export default TravelRequestForm;
